import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { LoaderCircleIcon } from 'lucide-react';
import { useEffect, useState, useRef, memo } from 'react';

import { mergeStrategyDetails } from './merge-strategy-dropdown';

import { ComponentBoundary } from '@/components/component-boundary';
import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useMergeStrategies } from '@/features/pulls/hooks/use-merge-strategies';
import { MergeStrategy, usePullMutations, mergeabilityQuery } from '@/features/pulls/services/pulls';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';

type MergeDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMergeStrategy: MergeStrategy;
};

const MergeDialogInner = memo(({ isOpen, onOpenChange, defaultMergeStrategy }: MergeDialogProps) => {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const z = useZero();
  const { isMerging, mergePull } = usePullMutations(owner, repo, number);

  const [pull] = useZeroQuery(
    z.query.PullsTable.where(({ and, cmp }) => and(cmp('owner', owner), cmp('repo', repo), cmp('number', number))).one()
  );

  const { data: mergeabilityStatus } = useSuspenseQuery(mergeabilityQuery(owner, repo, number));

  const { availableStrategies } = useMergeStrategies();

  const [mergeStrategy, setMergeStrategy] = useState<MergeStrategy>(defaultMergeStrategy);
  const [mergeTitle, setMergeTitle] = useState('');
  const [mergeMessage, setMergeMessage] = useState('');

  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && pull) {
      setMergeStrategy(defaultMergeStrategy);
      setMergeTitle(`${pull.title} (#${pull.number})`);
      setMergeMessage(pull.body ?? '');
    }
  }, [isOpen, pull, defaultMergeStrategy]);

  const wasMergingRef = useRef(isMerging);

  useEffect(() => {
    if (wasMergingRef.current && !isMerging && isOpen) {
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 1000);
      return () => clearTimeout(timer);
    }

    wasMergingRef.current = isMerging;
  }, [isMerging, isOpen, onOpenChange]);

  const handleTitleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Prevent text selection by setting cursor to end
    const input = e.target;
    setTimeout(() => {
      input.setSelectionRange(input.value.length, input.value.length);
    }, 0);
  };

  if (!pull) {
    return <div className="p-4">Loading pull request...</div>;
  }

  const handleMerge = async () => {
    try {
      await mergePull({
        mergeStrategy,
        mergeTitle,
        mergeMessage,
        sha: pull.head.sha,
      });
    } catch (error) {
      console.error('Failed to merge pull request:', error);
    }
  };

  // pull is now available
  const canMerge = mergeabilityStatus === 'MERGEABLE';

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (isMerging && !open) {
          return;
        }
        onOpenChange(open);
      }}
    >
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl">Merge Pull Request</DialogTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
            <Badge variant="secondary" className="font-mono">
              {pull.head.ref}
            </Badge>
            <span>→</span>
            <Badge variant="secondary" className="font-mono">
              {pull.base.ref}
            </Badge>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Commit Title Section */}
            <div className="space-y-2">
              <Label htmlFor="commit-title" className="text-sm font-medium">
                Commit Title
              </Label>
              <Input
                ref={titleInputRef}
                id="commit-title"
                value={mergeTitle}
                onChange={(e) => setMergeTitle(e.target.value)}
                onFocus={handleTitleFocus}
                placeholder="Enter commit title"
                className="w-full"
              />
            </div>

            {/* Merge Strategy Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Merge Strategy</Label>
              <div className="space-y-2">
                {/* Merge Option */}
                <button
                  onClick={() => setMergeStrategy('merge')}
                  disabled={!availableStrategies.includes('merge')}
                  className={cn(
                    'w-full text-left p-4 rounded-lg border-2 transition-all',
                    'hover:border-accent-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    mergeStrategy === 'merge' ? 'border-primary bg-primary/5' : 'border-border'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center',
                        mergeStrategy === 'merge' ? 'border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {mergeStrategy === 'merge' && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{mergeStrategyDetails.merge.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {mergeStrategyDetails.merge.description}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Squash Option */}
                <button
                  onClick={() => setMergeStrategy('squash')}
                  disabled={!availableStrategies.includes('squash')}
                  className={cn(
                    'w-full text-left p-4 rounded-lg border-2 transition-all',
                    'hover:border-accent-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    mergeStrategy === 'squash' ? 'border-primary bg-primary/5' : 'border-border'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center',
                        mergeStrategy === 'squash' ? 'border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {mergeStrategy === 'squash' && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{mergeStrategyDetails.squash.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {mergeStrategyDetails.squash.description}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Rebase Option */}
                <button
                  onClick={() => setMergeStrategy('rebase')}
                  disabled={!availableStrategies.includes('rebase')}
                  className={cn(
                    'w-full text-left p-4 rounded-lg border-2 transition-all',
                    'hover:border-accent-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    mergeStrategy === 'rebase' ? 'border-primary bg-primary/5' : 'border-border'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center',
                        mergeStrategy === 'rebase' ? 'border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {mergeStrategy === 'rebase' && <div className="h-2 w-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{mergeStrategyDetails.rebase.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {mergeStrategyDetails.rebase.description}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Commit Message */}
          <div className="space-y-2">
            <Label htmlFor="commit-message" className="text-sm font-medium">
              Commit Message
            </Label>
            <div className="border rounded-md min-h-[300px] max-h-[400px] overflow-auto">
              <MarkdownEditor
                value={mergeMessage}
                onChange={setMergeMessage}
                placeholder="Add a detailed description of the changes..."
                showStaticMenu={false}
                className="min-h-[300px] px-3"
                owner={owner}
                repo={repo}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="pt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isMerging}>
            Cancel
          </Button>
          <Button onClick={handleMerge} disabled={isMerging || !mergeTitle.trim()} className="min-w-[140px]">
            {isMerging ? (
              <>
                <LoaderCircleIcon className="size-4 mr-2 animate-spin" />
                Merging...
              </>
            ) : (
              'Merge Pull Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

const MergeDialog = ({ isOpen, onOpenChange, defaultMergeStrategy }: MergeDialogProps) => {
  return (
    <ComponentBoundary fallback={<Skeleton className="w-full rounded-md h-32" />} name="MergeDialog">
      <MergeDialogInner isOpen={isOpen} onOpenChange={onOpenChange} defaultMergeStrategy={defaultMergeStrategy} />
    </ComponentBoundary>
  );
};

export { MergeDialog };
